import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, ArrowRight, Calendar, User, Tag, Search, Filter } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: 'relationships' | 'ai-insights' | 'wellness' | 'guides';
  readTime: string;
  image: string;
  featured?: boolean;
  keywords?: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'The Science Behind AI-Powered Relationship Insights',
    excerpt: 'Discover how Gemini 2.5 Flash analyzes communication patterns to provide personalized relationship guidance that actually works.',
    author: 'Dr. Sarah Chen',
    date: '2025-01-15',
    category: 'ai-insights',
    readTime: '8 min read',
    image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg',
    featured: true,
    keywords: 'AI relationship insights, Gemini AI, relationship analysis, communication patterns, relationship science, AI technology, personalized advice, relationship psychology'
  },
  {
    id: '2',
    title: '5 VR Scenarios That Transformed Real Relationships',
    excerpt: 'Real stories from Octify users who used virtual reality role-play to overcome communication challenges and strengthen their bonds.',
    author: 'Marcus Rodriguez',
    date: '2025-01-12',
    category: 'relationships',
    readTime: '6 min read',
    image: 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg',
    keywords: 'VR role-play, virtual reality relationships, communication challenges, relationship improvement, VR therapy, relationship transformation, immersive experiences'
  },
  {
    id: '3',
    title: 'Understanding Your Heart Rate Variability and Relationships',
    excerpt: 'Learn how your wearable data correlates with relationship stress and discover techniques to improve both your health and connections.',
    author: 'Dr. Emily Watson',
    date: '2025-01-10',
    category: 'wellness',
    readTime: '10 min read',
    image: 'https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg',
    keywords: 'heart rate variability, wearable data, relationship stress, health monitoring, stress management, biometric data, wellness tracking, relationship health'
  },
  {
    id: '4',
    title: 'Complete Guide to Setting Up Family Relationship Goals',
    excerpt: 'Step-by-step instructions for creating meaningful family connection goals and tracking progress with Octify\'s AI insights.',
    author: 'Jennifer Park',
    date: '2025-01-08',
    category: 'guides',
    readTime: '12 min read',
    image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg',
    keywords: 'family relationships, relationship goals, family communication, parenting tips, family bonding, relationship planning, family dynamics, goal setting'
  },
  {
    id: '5',
    title: 'Why Your Pet\'s Behavior Reflects Your Emotional State',
    excerpt: 'Exploring the fascinating connection between human emotions and pet behavior, plus tips for strengthening your bond.',
    author: 'Dr. Michael Torres',
    date: '2025-01-05',
    category: 'relationships',
    readTime: '7 min read',
    image: 'https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg',
    keywords: 'pet relationships, human-animal bond, pet behavior, emotional connection, pet psychology, animal communication, pet bonding, emotional intelligence'
  },
  {
    id: '6',
    title: 'Workplace Relationships: AI Tips for Better Team Dynamics',
    excerpt: 'How to use Octify\'s insights to improve professional relationships and create a more collaborative work environment.',
    author: 'Lisa Chang',
    date: '2025-01-03',
    category: 'relationships',
    readTime: '9 min read',
    image: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg',
    keywords: 'workplace relationships, team dynamics, professional communication, office relationships, team collaboration, workplace psychology, leadership skills, work culture'
  }
];

const categories = [
  { id: 'all', label: 'All Posts', color: 'from-purple-500 to-indigo-500' },
  { id: 'relationships', label: 'Relationships', color: 'from-pink-500 to-rose-500' },
  { id: 'ai-insights', label: 'AI Insights', color: 'from-blue-500 to-cyan-500' },
  { id: 'wellness', label: 'Wellness', color: 'from-green-500 to-teal-500' },
  { id: 'guides', label: 'Guides', color: 'from-orange-500 to-yellow-500' }
];

interface BlogListProps {
  onReadPost?: (postId: string) => void;
}

export const BlogList: React.FC<BlogListProps> = ({ onReadPost }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const prefersReducedMotion = useReducedMotion();

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured || selectedCategory !== 'all' || searchQuery !== '');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat?.color || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #FF6B35 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #8B5CF6 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full mb-6">
            <Newspaper className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Relationship Insights Blog
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Expert advice, AI insights, and real stories to help you build stronger, more meaningful relationships.
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-12 pr-4 py-3 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/30 transition-all duration-200"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                      : 'bg-white/10 dark:bg-gray-800/10 text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/20'
                  }`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Featured Post */}
        {featuredPost && selectedCategory === 'all' && searchQuery === '' && (
          <motion.div
            className="mb-16"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Featured Article</h2>
            <motion.div
              className="group relative overflow-hidden rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300"
              whileHover={prefersReducedMotion ? {} : { y: -5 }}
            >
              <div className="grid lg:grid-cols-2 gap-8 p-8">
                <div className="order-2 lg:order-1">
                  <div className="flex items-center gap-4 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${getCategoryColor(featuredPost.category)} text-white`}>
                      {categories.find(c => c.id === featuredPost.category)?.label}
                    </span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-500/20 text-orange-600 dark:text-orange-400">
                      Featured
                    </span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-orange-500 transition-colors duration-300">
                    {featuredPost.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="flex items-center gap-6 mb-6 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.date)}
                    </div>
                    <span>{featuredPost.readTime}</span>
                  </div>
                  
                  <motion.button
                    onClick={() => onReadPost?.(featuredPost.id)}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium flex items-center gap-2"
                    whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                    whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                  >
                    Read Article
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </div>
                
                <div className="order-1 lg:order-2">
                  <div className="relative h-64 lg:h-full rounded-2xl overflow-hidden">
                    <img
                      src={featuredPost.image}
                      alt={`Featured article: ${featuredPost.title} - AI-powered relationship insights`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Regular Posts Grid */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            {selectedCategory === 'all' ? 'Latest Articles' : `${categories.find(c => c.id === selectedCategory)?.label} Articles`}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post, index) => (
              <motion.div
                key={post.id}
                className="group relative overflow-hidden rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { y: -5 }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={`${post.title} - ${post.category} article about relationship insights`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${getCategoryColor(post.category)} text-white`}>
                      {categories.find(c => c.id === post.category)?.label}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-orange-500 transition-colors duration-300 line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {post.author}
                    </div>
                    <span>{post.readTime}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.date)}
                    </div>
                    
                    <motion.button
                      onClick={() => onReadPost?.(post.id)}
                      className="text-orange-500 hover:text-orange-400 font-medium flex items-center gap-1"
                      whileHover={prefersReducedMotion ? {} : { x: 5 }}
                    >
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <motion.div
            className="text-center py-16"
            initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No articles found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Try adjusting your search or browse a different category.
            </p>
            <motion.button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              View All Articles
            </motion.button>
          </motion.div>
        )}

        {/* Newsletter Signup */}
        <motion.div
          className="mt-16 p-8 rounded-3xl bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-200/30 dark:border-orange-700/30 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Get the latest relationship insights and AI tips delivered to your inbox.
          </p>
          <motion.button
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-lg"
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            Subscribe to Newsletter
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};